/*
 * Super Admin — Users & Roles (Part 1).
 * Cross-store user search/filter, detail drawer, one-time password reset,
 * and activate/deactivate. Follows the Executive Clarity admin design.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, KeyRound, Copy, Check, RefreshCw, Eye, UserX, UserCheck, ShieldCheck, X, Upload, Trash2, FlaskConical, RotateCcw, ShieldAlert, Mail, Clock, Archive, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RoleAccessPanel } from "./RoleAccessPanel";
import { sandboxAdminApi } from "../../../services/phase1Api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const headersFor = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
});

const ACCOUNT_TYPES = ["School User", "Portfolio User", "Super Admin", "Client"];
const STATUSES = ["Active", "Password Reset Required", "Inactive"];
const STATUS_COLOR = {
  Active: "bg-emerald-100 text-emerald-800",
  "Password Reset Required": "bg-amber-100 text-amber-800",
  Inactive: "bg-gray-200 text-gray-700",
};

const initialsOf = (name = "", email = "") => {
  const src = (name || email || "U").trim();
  return src.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "U";
};

const AdminPhotoManager = ({ u, headers, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(u.profile_photo_url || null);
  const fileRef = React.useRef(null);
  const targetType = u.scope === "tenant" ? "tenant_user" : "user";
  const photo = url ? `${API}${url}` : null;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) { toast.error("Use PNG, JPG, or WebP."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("target_type", targetType); fd.append("target_id", u.id);
      const r = await axios.post(`${API}/api/assets/admin/profile-photo`, fd, { headers });
      setUrl(r.data.profile_photo_url);
      toast.success("Photo updated");
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/assets/admin/profile-photo`, { headers, params: { target_type: targetType, target_id: u.id } });
      setUrl(null); toast.success("Photo removed"); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3 mt-3" data-testid="admin-user-photo-manager">
      {photo ? (
        <img src={photo} alt={`${u.name || u.email} photo`} className="h-14 w-14 rounded-full object-cover border border-[#e5e7eb]" data-testid="admin-user-photo-img" />
      ) : (
        <div className="h-14 w-14 rounded-full grid place-items-center bg-[#0b1220] text-white text-sm font-semibold" data-testid="admin-user-photo-initials">{initialsOf(u.name, u.email)}</div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} data-testid="admin-user-photo-input" />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="admin-user-photo-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />{photo ? "Replace" : "Upload"}</>}
        </Button>
        {photo && <Button size="sm" variant="outline" className="text-red-600" disabled={busy} onClick={remove} data-testid="admin-user-photo-remove"><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export default function UsersAdminPage({ session }) {
  const headers = useMemo(() => headersFor(session), [session]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sbxEmails, setSbxEmails] = useState(new Set());
  const [forceTarget, setForceTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteScan, setDeleteScan] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [tempOpen, setTempOpen] = useState(false);
  const [tempList, setTempList] = useState(null);

  const loadSandbox = useCallback(() => {
    sandboxAdminApi.listAccess(session)
      .then((d) => setSbxEmails(new Set((d.access || []).filter((a) => a.enabled).map((a) => a.email))))
      .catch(() => {});
  }, [session]);
  useEffect(() => { loadSandbox(); }, [loadSandbox]);

  const toggleSandbox = async (u) => {
    const email = (u.email || "").toLowerCase();
    if (!email) return;
    const enable = !sbxEmails.has(email);
    setBusy(`sbx-${u.id}`);
    try {
      await sandboxAdminApi.setAccess(email, enable, session);
      toast.success(enable ? `Sandbox enabled for ${email}` : `Sandbox disabled for ${email}`);
      setSbxEmails((prev) => { const n = new Set(prev); enable ? n.add(email) : n.delete(email); return n; });
    } catch { toast.error("Failed to update sandbox access"); } finally { setBusy(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/users`, {
        headers, params: { search, account_type: accountType, status, page_size: 100 },
      });
      setRows(r.data.users || []); setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load users"); } finally { setLoading(false); }
  }, [headers, search, accountType, status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const openDetail = async (u) => {
    setDetail({ loading: true, row: u });
    try {
      const r = await axios.get(`${API}/api/admin/users/${u.scope}/${u.id}`, { headers });
      setDetail({ loading: false, row: r.data });
    } catch { toast.error("Failed to load user"); setDetail(null); }
  };

  const doReset = async () => {
    setBusy("reset");
    try {
      const r = await axios.post(`${API}/api/admin/users/${resetTarget.scope}/${resetTarget.id}/reset-password`, {}, { headers });
      setResetResult(r.data);
      load();
    } catch { toast.error("Reset failed"); } finally { setBusy(""); }
  };
  const copyPw = () => { if (resetResult?.temp_password) { navigator.clipboard.writeText(resetResult.temp_password); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
  const closeReset = () => { setResetTarget(null); setResetResult(null); setCopied(false); };

  const loadTemp = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/users/temp-passwords`, { headers });
      setTempList(r.data.accounts || []);
    } catch { toast.error("Failed to load temp passwords"); }
  }, [headers]);
  const openTemp = () => { setTempOpen(true); setTempList(null); loadTemp(); };

  const doForceReset = async () => {
    setBusy("force");
    try {
      const r = await axios.post(`${API}/api/admin/users/${forceTarget.scope}/${forceTarget.id}/force-reset`, { send_email: true }, { headers });
      const em = r.data?.email?.status;
      toast.success(`Force-reset set for ${forceTarget.email}${em === "sent" ? " · email sent" : ""}`);
      setForceTarget(null); load(); if (tempOpen) loadTemp();
    } catch { toast.error("Force reset failed"); } finally { setBusy(""); }
  };

  const openDelete = async (u) => {
    setDeleteTarget(u); setDeleteScan(null); setDeleteReason("");
    try {
      const r = await axios.get(`${API}/api/admin/users/${u.scope}/${u.id}/dependencies`, { headers });
      setDeleteScan(r.data);
    } catch { setDeleteScan({ error: true }); }
  };
  const doDelete = async (mode, force = false) => {
    setBusy("delete");
    try {
      await axios.post(`${API}/api/admin/users/${deleteTarget.scope}/${deleteTarget.id}/delete`,
        { mode, reason: deleteReason, confirm: true, force }, { headers });
      toast.success(mode === "archive" ? "User archived" : "User deleted");
      setDeleteTarget(null); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) toast.error("Has production data — archive it, or use Hard delete (override).");
      else toast.error(typeof detail === "string" ? detail : "Delete failed");
    } finally { setBusy(""); }
  };

  const toggleStatus = async (u) => {
    const makeActive = u.status === "Inactive";
    setBusy(u.id);
    try {
      await axios.post(`${API}/api/admin/users/${u.scope}/${u.id}/status`, { active: makeActive }, { headers });
      toast.success(makeActive ? "User reactivated" : "User deactivated");
      load();
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-[1300px] mx-auto" data-testid="users-admin">
      <div className="mb-5">
        <div className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[#9aa2b1]">Super Admin</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">Users &amp; Roles</h1>
        <p className="text-sm text-[#6b7280] mt-1">Search and manage users across all schools, portfolios, and account types. Reset passwords securely.</p>
      </div>

      <RoleAccessPanel />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, school, or portfolio" className="h-10 pl-9 bg-white" data-testid="users-search" />
        </div>
        <select value={accountType} onChange={(e) => setAccountType(e.target.value)} className="h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm" data-testid="users-filter-type">
          <option value="">All account types</option>
          {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-[#e5e7eb] bg-white px-3 text-sm" data-testid="users-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button variant="outline" onClick={openTemp} className="h-10" data-testid="users-temp-passwords-btn"><ListChecks className="h-4 w-4 mr-1.5" />Temp Passwords</Button>
        <Button variant="outline" onClick={load} className="h-10"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="text-[12px] text-[#6b7280] mb-2">{loading ? "Loading…" : `${total} user${total === 1 ? "" : "s"}`}</div>

      <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="users-table">
            <thead className="bg-[#f9fafb] text-[#6b7280] text-[11.5px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                <th className="text-left px-4 py-2.5 font-semibold">Account Type</th>
                <th className="text-left px-4 py-2.5 font-semibold">School / Portfolio</th>
                <th className="text-left px-4 py-2.5 font-semibold">Role</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-center px-4 py-2.5 font-semibold">Sandbox</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={`${u.scope}-${u.id}`} className="border-t border-[#f0f1f3] hover:bg-[#fafbfc]" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium text-[#111827]">{u.name || "—"}</td>
                  <td className="px-4 py-2.5 text-[#374151]">{u.email}</td>
                  <td className="px-4 py-2.5">{u.account_type}</td>
                  <td className="px-4 py-2.5 text-[#6b7280]">{u.tenant_name || u.portfolio_name || "—"}</td>
                  <td className="px-4 py-2.5 text-[#6b7280]">{u.role}</td>
                  <td className="px-4 py-2.5"><span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLOR[u.status] || "bg-gray-100"}`}>{u.status}</span></td>
                  <td className="px-4 py-2.5 text-center">
                    <Button size="sm" variant={sbxEmails.has((u.email || "").toLowerCase()) ? "default" : "ghost"}
                      className={`h-7 px-2 ${sbxEmails.has((u.email || "").toLowerCase()) ? "bg-indigo-600 hover:bg-indigo-700" : "text-[#6b7280]"}`}
                      disabled={busy === `sbx-${u.id}` || !u.email} onClick={() => toggleSandbox(u)}
                      title="Toggle Testing Sandbox access" data-testid={`user-sandbox-${u.id}`}>
                      {busy === `sbx-${u.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openDetail(u)} data-testid={`user-view-${u.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => { setResetTarget(u); setResetResult(null); }} data-testid={`user-reset-${u.id}`}><KeyRound className="h-3.5 w-3.5 mr-1" />Reset</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" title="Force change at next login" onClick={() => setForceTarget(u)} data-testid={`user-force-${u.id}`}><RotateCcw className="h-3.5 w-3.5 text-[#6b7280]" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy === u.id} onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.id}`}>
                        {u.status === "Inactive" ? <UserCheck className="h-3.5 w-3.5 text-emerald-600" /> : <UserX className="h-3.5 w-3.5 text-red-500" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" title="Delete / archive" onClick={() => openDelete(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6b7280]">No users match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset password modal */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && closeReset()}>
        <DialogContent className="max-w-md bg-white" data-testid="user-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>{resetTarget?.email}</DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <>
              <p className="text-[13px] text-[#374151]">This generates a new temporary password, emails it to the user, and requires them to change it at next login. All active sessions are signed out. The existing password is never revealed.</p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeReset}>Cancel</Button>
                <Button disabled={busy === "reset"} onClick={doReset} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="user-reset-confirm">
                  {busy === "reset" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}Generate &amp; email temporary password
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-3" data-testid="user-reset-result">
              <div className="rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] p-3 text-[13px] text-[#065f46]">
                Temporary password generated. Copy it now. For security, it will not be shown again.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded bg-[#f9fafb] border border-[#e5e7eb] text-[14px]" data-testid="user-reset-password">{resetResult.temp_password}</code>
                <Button variant="outline" onClick={copyPw} data-testid="user-reset-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
              </div>
              <div className="text-[12px] text-[#6b7280] flex items-center gap-1.5" data-testid="user-reset-email-status">
                <Mail className="h-3.5 w-3.5" />
                {resetResult.email?.status === "sent" ? <span className="text-emerald-700">Emailed to user (traceable in Emails)</span>
                  : resetResult.email?.status === "not_sent" ? <span>Email not sent</span>
                  : <span className="text-amber-700">Email {resetResult.email?.status || "captured"} — check Emails page</span>}
              </div>
              <p className="text-[12px] text-[#6b7280]">Login: {resetResult.login_url} · Expires in {resetResult.expires_in_hours}h · User must change at next login.</p>
              <DialogFooter><Button onClick={closeReset} className="bg-[#0b1220] hover:bg-black text-white">Done</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Force-reset modal (change on next login, keeps current password valid) */}
      <Dialog open={!!forceTarget} onOpenChange={(v) => !v && setForceTarget(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="user-force-modal">
          <DialogHeader>
            <DialogTitle>Force password change</DialogTitle>
            <DialogDescription>{forceTarget?.email}</DialogDescription>
          </DialogHeader>
          <p className="text-[13px] text-[#374151]">
            The user keeps their current password to sign in, but will be required to choose a new one immediately after. Active sessions are signed out and a notice is emailed. No temporary password is generated.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setForceTarget(null)}>Cancel</Button>
            <Button disabled={busy === "force"} onClick={doForceReset} className="bg-[#0b1220] hover:bg-black text-white" data-testid="user-force-confirm">
              {busy === "force" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}Force change at next login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / archive modal with dependency scan */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="user-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-500" />Delete user</DialogTitle>
            <DialogDescription>{deleteTarget?.email}</DialogDescription>
          </DialogHeader>
          {!deleteScan ? (
            <div className="py-8 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Scanning dependencies…</div>
          ) : deleteScan.error ? (
            <div className="text-[13px] text-red-600">Could not scan dependencies.</div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3" data-testid="user-delete-scan">
                <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#6b7280] mb-1.5">Linked records</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                  {Object.entries(deleteScan.dependencies || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="text-[#6b7280] capitalize">{k.replace(/_/g, " ")}</span><span className={`font-semibold ${v > 0 ? "text-[#111827]" : "text-[#9ca3af]"}`}>{v}</span></div>
                  ))}
                </div>
              </div>
              {deleteScan.safe_to_hard_delete ? (
                <div className="rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] p-2.5 text-[12px] text-[#065f46]">
                  {deleteScan.is_test_account ? "Test account — safe to hard delete." : "No production data — safe to hard delete."}
                </div>
              ) : (
                <div className="rounded-lg border border-[#fde68a] bg-[#fffbeb] p-2.5 text-[12px] text-[#92400e]">
                  This account has production data ({deleteScan.production_dependency_count} record(s)). <strong>Archive</strong> is recommended so nothing orphans. Hard delete requires an override.
                </div>
              )}
              <Input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Reason (recorded in the audit log)" className="h-10 bg-white" data-testid="user-delete-reason" />
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                <Button disabled={busy === "delete"} onClick={() => doDelete("archive")} className="bg-[#0b1220] hover:bg-black text-white" data-testid="user-delete-archive">
                  <Archive className="h-4 w-4 mr-1.5" />Archive (reversible)
                </Button>
                {deleteScan.safe_to_hard_delete ? (
                  <Button disabled={busy === "delete"} onClick={() => doDelete("delete")} className="bg-red-600 hover:bg-red-700 text-white" data-testid="user-delete-hard">
                    <Trash2 className="h-4 w-4 mr-1.5" />Hard delete
                  </Button>
                ) : (
                  <Button disabled={busy === "delete"} onClick={() => doDelete("delete", true)} variant="outline" className="border-red-300 text-red-600" data-testid="user-delete-hard-force">
                    <Trash2 className="h-4 w-4 mr-1.5" />Hard delete (override)
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temp passwords panel */}
      <Dialog open={tempOpen} onOpenChange={(v) => !v && setTempOpen(false)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="temp-passwords-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#007a9e]" />Active temp passwords &amp; forced resets</DialogTitle>
            <DialogDescription>Accounts that must set a new password. Reset re-issues; Force changes at next login.</DialogDescription>
          </DialogHeader>
          {!tempList ? (
            <div className="py-8 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : tempList.length === 0 ? (
            <div className="py-8 text-center text-[#6b7280]">No accounts pending a password change.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-2">
              <table className="w-full text-[13px]" data-testid="temp-passwords-table">
                <thead className="text-[11.5px] uppercase text-[#6b7280]"><tr>
                  <th className="text-left px-2 py-2">User</th><th className="text-left px-2 py-2">Type</th>
                  <th className="text-left px-2 py-2">Expires</th><th className="text-right px-2 py-2">Actions</th>
                </tr></thead>
                <tbody>
                  {tempList.map((t) => (
                    <tr key={`${t.scope}-${t.id}`} className="border-t border-[#f0f1f3]" data-testid={`temp-row-${t.id}`}>
                      <td className="px-2 py-2"><div className="font-medium text-[#111827]">{t.name || t.email}</div><div className="text-[#9ca3af] text-[12px]">{t.email}</div></td>
                      <td className="px-2 py-2">{t.kind === "temp_password" ? "Temp password" : "Forced next login"}</td>
                      <td className="px-2 py-2">
                        {t.kind !== "temp_password" ? <span className="text-[#9ca3af]">—</span>
                          : t.expired ? <span className="text-red-600 font-semibold">Expired</span>
                          : t.expires_at ? new Date(t.expires_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="h-7" onClick={() => { setTempOpen(false); setResetTarget({ scope: t.scope, id: t.id, email: t.email }); setResetResult(null); }} data-testid={`temp-reset-${t.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => { setTempOpen(false); setForceTarget({ scope: t.scope, id: t.id, email: t.email }); }} data-testid={`temp-force-${t.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-detail-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl p-6">
            <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-[#9ca3af] hover:text-[#111827]"><X className="h-5 w-5" /></button>
            {detail.loading ? <div className="py-20 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (() => {
              const u = detail.row;
              const Line = ({ k, v }) => v ? <div className="flex justify-between gap-4 py-1.5 border-b border-[#f3f4f6] text-[13px]"><span className="text-[#6b7280]">{k}</span><span className="text-[#111827] font-medium text-right">{v}</span></div> : null;
              return (
                <div>
                  <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#007a9e]" /><h2 className="text-lg font-bold text-[#111827]">{u.name || u.email}</h2></div>
                  <span className={`inline-block mt-2 text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLOR[u.status] || "bg-gray-100"}`}>{u.status}</span>
                  <AdminPhotoManager u={u} headers={headers} onChanged={load} />
                  <div className="mt-4">
                    <Line k="Email" v={u.email} />
                    <Line k="Account Type" v={u.account_type} />
                    <Line k="Role" v={u.role} />
                    <Line k="School / Tenant" v={u.tenant_name} />
                    <Line k="Portfolio" v={u.portfolio_name} />
                    <Line k="Phone" v={u.phone} />
                    <Line k="Title" v={u.title} />
                    <Line k="User ID" v={u.id} />
                    <Line k="Must change password" v={u.must_change_password ? "Yes" : null} />
                    <Line k="Associated Quote" v={u.associated_quote?.quote_number} />
                    <Line k="Associated PO" v={u.associated_po?.po_number} />
                    <Line k="License" v={u.associated_license ? `${u.associated_license.license_type} · ${u.associated_license.status}` : null} />
                  </div>
                  {Array.isArray(u.audit_history) && u.audit_history.length > 0 && (
                    <div className="mt-5">
                      <div className="text-[11.5px] font-semibold uppercase tracking-wide text-[#6b7280] mb-2">Recent Audit History</div>
                      <div className="space-y-1.5">
                        {u.audit_history.slice(0, 10).map((a, i) => (
                          <div key={i} className="text-[12px] text-[#374151] flex justify-between gap-2">
                            <span>{a.field_changed || a.action_type}</span>
                            <span className="text-[#9ca3af]">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button className="mt-5 w-full bg-[#007a9e] hover:bg-[#00607d] text-white" onClick={() => { setResetTarget(detail.row); setResetResult(null); setDetail(null); }} data-testid="user-detail-reset">
                    <KeyRound className="h-4 w-4 mr-2" />Reset password
                  </Button>
                  <div className="mt-2 flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setForceTarget({ scope: u.scope, id: u.id, email: u.email }); setDetail(null); }} data-testid="user-detail-force">
                      <RotateCcw className="h-4 w-4 mr-2" />Force change
                    </Button>
                    <Button variant="outline" className="flex-1 border-red-300 text-red-600" onClick={() => { const t = { scope: u.scope, id: u.id, email: u.email }; setDetail(null); openDelete(t); }} data-testid="user-detail-delete">
                      <Trash2 className="h-4 w-4 mr-2" />Delete
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
